import { useCallback, useRef, useState } from "react";
import type { TReviewLineItem } from "./types";
import type { TApiLineItemQuery } from "@/apis/proda_finance/types";

type TUseAutoPopulateDeps = {
    lineItems: TReviewLineItem[];
    filteredLineItems: TReviewLineItem[] | null;
    /** Server-side count of rows matching the active filter (whole dataset). */
    matchingRowCount: number;
    /** Active column filter mapped to API query params (no skip/limit). */
    activeFilterQuery: TApiLineItemQuery;
    onAutoPopulatePropcodes: (propcode: string, lineItemIds: string[]) => Promise<number>;
    onAutoPopulatePropcodesByFilter: (
        filterQuery: TApiLineItemQuery,
        propcode: string,
    ) => Promise<number>;
    /** Server-side master property-code search: called with the typed query string. */
    searchMasterPropertyCodes: (query: string) => Promise<Array<{ value: string; label: string }>>;
    onRefresh: () => void;
    /** Surface a success message (e.g. a toast/banner) once an auto-populate completes. */
    onSuccess?: (message: string) => void;
};

/**
 * Encapsulates the propcode auto-populate modal: open/close state, lookup-option
 * loading, and the submit handler. Extracted from ViewFileReview.
 */
export function useAutoPopulate({
    lineItems: _lineItems,
    filteredLineItems: _filteredLineItems,
    matchingRowCount,
    activeFilterQuery,
    onAutoPopulatePropcodes: _onAutoPopulatePropcodes,
    onAutoPopulatePropcodesByFilter,
    searchMasterPropertyCodes,
    onRefresh,
    onSuccess,
}: TUseAutoPopulateDeps) {
    const [isAutoPopulateOpen, setIsAutoPopulateOpen] = useState(false);
    // Full selected option (label "code - description") for the propcode SearchableSelect.
    const [selectedPropcodeOption, setSelectedPropcodeOption] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const selectedPropcode = selectedPropcodeOption?.value ?? "";
    const [autoPopulateError, setAutoPopulateError] = useState<string | null>(null);
    const [isAutoPopulating, setIsAutoPopulating] = useState(false);

    // Keep the latest callback refs so effects don't need to re-run when the
    // parent passes a new function identity on each render (e.g. in tests or
    // when useCases() is called inside the component body).
    const searchMasterPropertyCodesRef = useRef(searchMasterPropertyCodes);
    searchMasterPropertyCodesRef.current = searchMasterPropertyCodes;

    const handleAutoPopulateOpenChange = useCallback((open: boolean) => {
        setIsAutoPopulateOpen(open);
        if (!open) {
            setSelectedPropcodeOption(null);
            setAutoPopulateError(null);
        }
    }, []);

    /**
     * Stable search callback for the propcode SearchableSelect: delegates to the
     * server-side master property-code search so the query matches the full company
     * master set (code OR name), not just a client-loaded page.
     */
    const searchPropcodesForModal = useCallback(
        (query: string) => searchMasterPropertyCodesRef.current(query),
        [],
    );

    const handleAutoPopulatePropcodes = useCallback(async () => {
        if (!selectedPropcode) {
            setAutoPopulateError("Select a propcode before auto-populating.");
            return;
        }
        if (matchingRowCount === 0) {
            setAutoPopulateError("No line items match the current filter.");
            return;
        }
        setIsAutoPopulating(true);
        setAutoPopulateError(null);
        try {
            const updated = await onAutoPopulatePropcodesByFilter(
                activeFilterQuery,
                selectedPropcode,
            );
            handleAutoPopulateOpenChange(false);
            onSuccess?.(
                `Propcode auto-populated on ${updated} line item${updated === 1 ? "" : "s"}.`,
            );
            onRefresh();
        } catch (error) {
            setAutoPopulateError(
                error instanceof Error ? error.message : "Failed to auto-populate propcodes.",
            );
        } finally {
            setIsAutoPopulating(false);
        }
    }, [
        selectedPropcode,
        matchingRowCount,
        activeFilterQuery,
        onAutoPopulatePropcodesByFilter,
        handleAutoPopulateOpenChange,
        onRefresh,
        onSuccess,
    ]);

    return {
        isAutoPopulateOpen,
        setIsAutoPopulateOpen,
        handleAutoPopulateOpenChange,
        selectedPropcode,
        selectedPropcodeOption,
        setSelectedPropcodeOption,
        autoPopulateError,
        isAutoPopulating,
        handleAutoPopulatePropcodes,
        /** Stable search callback for the propcode SearchableSelect — server-side master property-code search. */
        searchPropcodesForModal,
    };
}
